import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { buttonVariants } from '@/lib/button-variants';
import { cn } from '@/lib/utils';

type PaginationTabsProps = {
  totalCount: number;
  limit: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
};

export default function PaginationTabs({
  totalCount,
  limit,
  currentPage,
  onPageChange,
  className,
}: PaginationTabsProps) {
  const totalPages = Math.ceil(totalCount / limit);

  if (totalPages <= 1) {
    return null;
  }

  const getPages = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    const pages: (number | string)[] = [];
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage === 0) {
      endPage = 2;
    } else if (currentPage === totalPages - 1) {
      startPage = totalPages - 3;
    }

    if (startPage > 0) {
      pages.push(0);

      if (startPage > 1) {
        pages.push('ellipsis-start');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages - 1);
    }

    return pages;
  };

  const pages = getPages();

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            className={cn('border cursor-pointer', {
              'pointer-events-none opacity-50': currentPage === 0,
            })}
            onClick={(e) => {
              e.preventDefault();

              if (currentPage > 0) {
                onPageChange(currentPage - 1);
              }
            }}
          />
        </PaginationItem>

        {pages.map((page) => {
          if (typeof page === 'string') {
            return (
              <PaginationItem key={page}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          const isActive = page === currentPage;

          return (
            <PaginationItem key={page}>
              <PaginationLink
                className={cn('cursor-pointer', {
                  'text-white!': isActive,
                  [buttonVariants({
                    variant: 'default',
                    className: 'shadow-none! hover:text-primary-foreground',
                  })]: isActive,
                  'border-none! bg-transparent! shadow-none! hover:bg-transparent!': !isActive,
                })}
                isActive={isActive}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(page);
                }}
              >
                {page + 1}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        <PaginationItem>
          <PaginationNext
            className={cn('border cursor-pointer', {
              'pointer-events-none opacity-50': currentPage === totalPages - 1,
            })}
            onClick={(e) => {
              e.preventDefault();

              if (currentPage < totalPages - 1) {
                onPageChange(currentPage + 1);
              }
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
